import React, { FC, PropsWithChildren } from "react";
import { Route } from "react-router";
import { useTranslation } from "react-i18next";
import { Divider, Grid, Group } from "@mantine/core";
import { EconomySettings } from "./EconomySettings";

import SettingsNavigation from "../../molecules/SettingsNavigation";

import UserSettings from "./User";
import UsersSettings from "./Users";
import CompanySpecificServices from "./Services";
import DepartmentSettings from "./DepartmentSettings";
import LogisticsSettings from "@/components/pages/Settings/LogisticsSettings";
import CompanySettings from "@/components/pages/Settings/CompanySettings";
import Templates from "@/components/pages/Settings/Templates";
import DeviationSettings from "@/components/pages/Settings/DeviationSettings";
import ContractArchive from "@/components/pages/Settings/ContractArchive";
import Customers from "@/components/pages/Settings/Customers";
import DocumentSettings from "@/components/pages/Settings/DocumentSettings";
import Product from "@/components/pages/Settings/Product";
import { Navigate, Routes } from "react-router-dom";
import { SidebarLayoutContainer, SidebarLayoutContent, SidebarLayoutMenu } from "@/components/atoms/SidebarLayout";
import styles from "./styles.module.css";
import Cameras from "./Cameras";
import TransportDevices from "./TransportDevices";

const preloadComponents = {
  user: () => import("./User"),
  company: () => import("./CompanySettings"),
  users: () => import("./Users"),
  services: () => import("./Services"),
  economy: () => import("./EconomySettings"),
  logistics: () => import("./LogisticsSettings"),
  "transport-devices": () => import("./TransportDevices"),
  deviations: () => import("./DeviationSettings"),
  products: () => import("./Product"),
  customers: () => import("./Customers"),
  templates: () => import("./Templates"),
  document: () => import("./DocumentSettings"),
  cameras: () => import("./Cameras"),
};

export default function Settings() {
  const { t } = useTranslation();

  return (
    <>
      <title>{`Reviver - ${t("settings:settings")}`}</title>
      <meta name="description" content={`Reviver - ${t("settings:settings")}`} />

      <SidebarLayoutContainer padded>
        <SidebarLayoutMenu>
          <SettingsNavigation preloadComponents={preloadComponents} />
        </SidebarLayoutMenu>
        <SidebarLayoutContent stretch={false}>
          <Routes>
            <Route path="account" element={<UserSettings />} />
            <Route path={`company`} element={<CompanySettings />} />
            <Route path={`departments`} element={<DepartmentSettings />} />
            <Route path={`logistics`} element={<LogisticsSettings />} />
            <Route path={`transport-devices`} element={<TransportDevices />} />
            <Route path={`economy`} element={<EconomySettings />} />
            <Route path={`products`} element={<Product />} />
            <Route path={`services`} element={<CompanySpecificServices />} />
            <Route path={`deviation`} element={<DeviationSettings />} />
            {/* disabled untill reviver implementing open market <Route path={`network`} element={<NetworkSettings/>} /> */}
            <Route path={`users`} element={<UsersSettings />} />
            <Route path={`templates`} element={<Templates />} />
            <Route path={`customers`} element={<Customers />} />
            <Route path={`contract-archive`} element={<ContractArchive />} />
            <Route path={`documents`} element={<DocumentSettings />} />
            <Route path={`cameras`} element={<Cameras />} />
            <Route path="/" element={<Navigate to="company" replace />} />
          </Routes>
        </SidebarLayoutContent>
      </SidebarLayoutContainer>
    </>
  );
}

export const SettingsFormContent: FC<PropsWithChildren> = (props) => (
  <div className={styles.settingsFormContent} {...props} />
);
export const SettingsFormCTAs: FC<PropsWithChildren> = (props) => (
  <div className={styles.settingsFormCtas} {...props} />
);

export const SettingsHeader: FC<PropsWithChildren<{ showDivider?: boolean }>> = ({ showDivider = true, children }) => (
  <React.Fragment>
    <Grid.Col span={12}>
      <Group justify={"space-between"} align={"center"} style={{ minHeight: "1.4rem" }}>
        {children}
      </Group>
    </Grid.Col>
    {showDivider && (
      <Grid.Col span={12}>
        <Divider />
      </Grid.Col>
    )}
  </React.Fragment>
);
